/**
 * Presentation contract for provisioning stages.
 *
 * The API owns the *lifecycle* (which stages run, in what order, with what
 * status). The console owns the *presentation* (what label/description the
 * user sees, whether the stage is exposed at all). The bridge between them is
 * the stable `stage` slug: the API guarantees the slug, the console maps it to
 * i18n keys + a visibility flag.
 *
 * Adding a stage on the backend? It will render automatically using the
 * `label` the API streams, until/unless an entry is added here. Removing or
 * renaming a slug is a backend versioning concern, not a copy tweak — the
 * mapping in this file is keyed by slug.
 */
export type StagePresentation = {
  titleId: string;
  descriptionId?: string;
  visible: boolean;
  // When true, this entry's title is used only inside the full status panel.
  // The compact card on the org-projects page falls back to the API-supplied
  // label so its copy stays unchanged.
  panelOnly?: boolean;
};

const HIDDEN: StagePresentation = { titleId: "", visible: false };

export const STAGE_PRESENTATION: Record<string, StagePresentation> = {
  create: {
    titleId: "project.creation.stage.create.title",
    descriptionId: "project.creation.stage.create.description",
    visible: true
  },
  activate: {
    titleId: "project.creation.stage.activate.title",
    descriptionId: "project.creation.stage.activate.description",
    visible: true
  },
  capabilities: {
    titleId: "project.creation.stage.capabilities.title",
    descriptionId: "project.creation.stage.capabilities.description",
    visible: true
  },
  integrations: {
    titleId: "project.creation.stage.integrations.title",
    descriptionId: "project.creation.stage.integrations.description",
    visible: true
  },
  grants: {
    titleId: "project.creation.stage.grants.title",
    descriptionId: "project.creation.stage.grants.description",
    visible: true
  },
  ready: {
    titleId: "project.creation.stage.ready.title",
    visible: true,
    panelOnly: true
  },
  variables: HIDDEN,
  profiles: HIDDEN,
  init: HIDDEN
};

export const getStagePresentation = (
  stage: string
): StagePresentation | undefined => STAGE_PRESENTATION[stage];

// Unknown stages default to visible so a newly-added backend stage degrades
// gracefully (it renders with the API-supplied label) instead of disappearing
// from the timeline.
export const isStageVisible = (stage: string): boolean =>
  STAGE_PRESENTATION[stage]?.visible ?? true;

// Used by the panel as a pre-stream placeholder so the timeline paints
// immediately instead of flashing an empty body until the first `data` event
// lands. Derived from STAGE_PRESENTATION's declaration order so a new visible
// stage added to the record above is picked up here automatically.
export const VISIBLE_STAGES_ORDER: ReadonlyArray<string> = Object.entries(
  STAGE_PRESENTATION
)
  .filter(([, presentation]) => presentation.visible)
  .map(([slug]) => slug);
